import { defineStore } from 'pinia';
import {
  login as apiLogin,
  fetchProfile as apiFetchProfile,
  refreshToken as apiRefreshToken,
} from '@/api/index';
import { resolveAvatarUrl } from '@/utils/avatar';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: localStorage.getItem('token') || null,
    refreshToken: localStorage.getItem('refresh_token') || null,
    username: localStorage.getItem('username') || '',
    role: localStorage.getItem('role') || '',
    avatar: resolveAvatarUrl(localStorage.getItem('avatar') || ''),
  }),

  getters: {
    isLoggedIn: (state) => !!state.token,
    isPremium: (state) => state.role === 'premium',
  },

  actions: {
    syncToLocalStorage() {
      if (this.token) {
        localStorage.setItem('token', this.token);
      } else {
        localStorage.removeItem('token');
      }
      if (this.refreshToken) {
        localStorage.setItem('refresh_token', this.refreshToken);
      } else {
        localStorage.removeItem('refresh_token');
      }
      if (this.username) {
        localStorage.setItem('username', this.username);
      } else {
        localStorage.removeItem('username');
      }
      if (this.role) {
        localStorage.setItem('role', this.role);
      } else {
        localStorage.removeItem('role');
      }
      if (this.avatar) {
        localStorage.setItem('avatar', this.avatar);
      } else {
        localStorage.removeItem('avatar');
      }
    },

    async login(username, password) {
      const response = await apiLogin(username, password);
      const data = response.data;

      if (!data.access) {
        throw new Error('Login berhasil, tetapi data tidak lengkap. Silakan coba lagi.');
      }

      this.token = data.access;
      this.refreshToken = data.refresh || null;
      this.username = data.username || username;
      this.role = data.role || 'guest';
      this.avatar = resolveAvatarUrl(data.avatar || '');
      this.syncToLocalStorage();

      // Fetch author profile if writer
      if (data.role === 'writer') {
        try {
          const { default: api } = await import('@/api/index');
          const profileResponse = await api.get('/api/author-profiles/me/', {
            headers: { Authorization: `Bearer ${this.token}` },
          });
          if (profileResponse.data && profileResponse.data.id) {
            localStorage.setItem('author_profile_id', profileResponse.data.id);
          }
        } catch (e) {
          // Profile fetch is best-effort
        }
      }

      return data;
    },

    async fetchProfile() {
      const response = await apiFetchProfile();
      const data = response.data;

      if (data) {
        this.username = data.username || this.username;
        this.role = data.role || this.role;
        let avatarRaw = data.avatar || '';
        // Untuk penulis, foto profil ada di author profile (photo), bukan user.avatar
        if (this.role === 'writer' && !avatarRaw) {
          try {
            const { fetchAuthorProfile } = await import('@/api/index');
            const authorRes = await fetchAuthorProfile();
            avatarRaw = (authorRes.data && authorRes.data.photo) || '';
          } catch (e) {
            // Best-effort — pertahankan nilai yang sudah ada
          }
        }
        this.avatar = resolveAvatarUrl(avatarRaw);
        this.syncToLocalStorage();
      }
      return data;
    },

    async refreshAccessToken() {
      if (!this.refreshToken) {
        throw new Error('No refresh token available');
      }
      const response = await apiRefreshToken(this.refreshToken);
      const data = response.data;
      if (data.access) {
        this.token = data.access;
        if (data.refresh) {
          this.refreshToken = data.refresh;
        }
        this.syncToLocalStorage();
      }
      return data;
    },

    async updateProfile(payload) {
      const { updateProfile: apiUpdateProfile } = await import('@/api/index');
      const response = await apiUpdateProfile(payload);
      const data = response.data;

      if (data.username) this.username = data.username;
      if (data.role) this.role = data.role;
      if (data.avatar) this.avatar = resolveAvatarUrl(data.avatar);
      this.syncToLocalStorage();

      return data;
    },

    setProfile(data) {
      if (data.username) this.username = data.username;
      if (data.role) this.role = data.role;
      if (data.avatar) this.avatar = resolveAvatarUrl(data.avatar);
      this.syncToLocalStorage();
    },

    setTokens(token, refreshToken) {
      this.token = token;
      this.refreshToken = refreshToken || this.refreshToken;
      this.syncToLocalStorage();
    },

    logout() {
      this.token = null;
      this.refreshToken = null;
      this.username = '';
      this.role = '';
      this.avatar = '';
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('username');
      localStorage.removeItem('role');
      localStorage.removeItem('avatar');
      localStorage.removeItem('author_profile_id');
    },
  },
});
